<template>
  <vl-popover
    v-if="filteredItems && filteredItems.length"
    ref="popover"
    :label-close-aria="labelCloseAria"
    :class="{ 'popover--large': modLarge }"
  >
    <template v-slot:label>
      <slot name="button" v-bind="{ clickHandler: onActionButtonClick }">
        <vl-button
          v-if="modVertical"
          mod-icon
          mod-secondary
          mod-naked
          icon="nav-show-more-vertical"
          class="action-button action-button--vertical"
          @click="onActionButtonClick"
        />
        <vl-button
          v-else
          mod-icon-before
          :mod-primary="modPrimary"
          :mod-secondary="modSecondary"
          icon="play"
          class="action-button"
          @click="onActionButtonClick"
        >
          {{ popoverLabel.text }}</vl-button
        >
      </slot>
    </template>
    <div class="vl-popover-content">
      <vl-link-list>
        <template v-for="item in filteredItems" :key="item">
          <vl-link v-if="!item.loading" mod-block @click.prevent="onItemClick($event, item)">{{
            itemLabel(item)
          }}</vl-link>
          <div v-else mod-stacked class="vl-u-align-left">
            <p>
              <vl-button mod-link mod-disabled>{{ itemLabel(item) }}</vl-button>
              <vl-loader mod-inline class="loader--item" />
            </p>
          </div>
        </template>
      </vl-link-list>
    </div>
  </vl-popover>
</template>

<script lang="ts" setup>
import { computed, PropType, reactive, ref, watch } from 'vue';

export type PbsActionButtonItem = {
  name?: string;
  label: string;
  disabled?: boolean;
  click: (e: any) => Promise<void> | void;
  loading?: boolean;
  ellipsis?: boolean;
};

const popover = ref(null);

const props = defineProps({
  modDisabled: {
    type: Boolean,
    required: false
  },
  modPrimary: {
    type: Boolean,
    required: false,
    default: true
  },
  modSecondary: {
    type: Boolean,
    required: false,
    default: false
  },
  modVertical: {
    type: Boolean,
    required: false,
    default: false
  },
  modLarge: {
    type: Boolean,
    required: false,
    default: false
  },
  label: {
    type: String,
    required: true,
    default: 'Meer acties'
  },
  labelCloseAria: {
    type: String,
    required: false,
    default: 'Sluiten'
  },
  items: {
    type: Array as PropType<PbsActionButtonItem[]>,
    required: false,
    default: () => []
  }
});

const data = reactive({
  items: props.items.map((x) => x)
});

const onActionButtonClick = (e: any) => {
  e.stopPropagation();
  openPopover();
};

const onItemClick = async (e: any, item: PbsActionButtonItem) => {
  e.stopPropagation();
  try {
    item.loading = true;
    await item?.click(e);
  } finally {
    item.loading = false;
    closePopover();
  }
};

const closePopover = () => {
  const popoverEl = popover.value;
  if (popoverEl) {
    popoverEl.closePopover();
  }
};

const openPopover = () => {
  const popoverEl = popover.value;
  if (popoverEl) {
    popoverEl.openPopover();
  }
};

const filteredItems = computed(() => {
  return data.items?.filter((x) => !x.disabled);
});

watch(
  () => props.items,
  (newValue) => {
    data.items = newValue.map((x) => x);
  }
);

const popoverLabel = computed(() => {
  const label = {
    text: props.label
  };
  return label;
});

const itemLabel = (item: PbsActionButtonItem) => {
  if (item.ellipsis) {
    return item.label + '...';
  }
  return item.label;
};
</script>

<style lang="scss" scoped>
.vl-link[disabled] {
  color: #687483;
  text-decoration: none;
}
.action-button--vertical {
  min-height: auto;
  margin-top: -1px;
}
.loader--item {
  margin-left: 10px;
}
</style>
<style lang="scss">
.popover--large .vl-popover__content {
  min-width: 28rem !important;
}
</style>
